import { UserRole } from './auth';
import { FieldValue } from 'firebase/firestore';

// Layout Profile Types
export type LayoutType = 'sidebar' | 'simple-list';

export interface LayoutProfile {
  id: string;
  name: string;
  roles: UserRole[];
  layoutType: LayoutType;
  // For sidebar layout, uses the main sidebar_config
  // For simple-list, we use a flat list of accessible pages
  sidebarConfigId?: string; // Optional: reference to a specific sidebar config
  customSidebarItems?: SidebarItem[]; // Optional: custom items for this profile
  fontConfig?: SidebarFontConfig;
  isDefault?: boolean; // Fallback layout if no profile matches
  order: number; // Priority order when user has multiple matching roles
  createdAt: Date | FieldValue;
  updatedAt: Date | FieldValue;
}

export interface LayoutProfilesConfig {
  profiles: LayoutProfile[];
  updatedAt: Date | FieldValue;
}

export interface BaseSidebarItem {
  id: string;
  name: string;
  icon?: string;  // Store icon name as string
  roles: UserRole[];
  order: number;
  clickable?: boolean;
  parentId?: string;  // For nested items
  hidden?: boolean;  // Hide from all users if true
  textColor?: 'white' | 'red' | 'yellow' | 'orange' | 'green' | 'blue' | 'purple' | 'pink' | 'teal' | 'indigo' | 'rose' | 'slate' | 'cyan' | 'lime' | 'amber' | 'emerald' | 'violet' | 'fuchsia' | 'sky' | 'gray' | 'stone' | 'zinc' | 'neutral' | 'black' | 'crimson' | 'gold' | 'silver' | 'bronze' | 'mint' | 'coral' | 'ruby' | 'sapphire' | 'topaz' | 'onyx' | 'pearl' | 'platinum' | 'copper' | 'steel' | 'iron' | 'titanium' | 'neon' | 'electric' | 'glow' | 'flame' | 'ice' | 'storm' | 'sunset' | 'dawn' | 'midnight' | 'rainbow' | 'aurora' | 'gradient-fire' | 'gradient-ocean' | 'gradient-sunset' | 'gradient-aurora' | 'gradient-cosmic' | 'gradient-forest' | 'gradient-royal' | 'gradient-neon' | 'gradient-ice' | 'gradient-volcano' | 'gradient-galaxy' | 'gradient-rainbow' | 'custom';
  customTextColor?: string; // Hex color for when textColor is 'custom'
}

export interface SidebarLink extends BaseSidebarItem {
  type: 'link';
  path: string;
  children?: never;
}

export interface SidebarFolder extends BaseSidebarItem {
  type: 'folder';
  path?: never;
  children: SidebarItem[];
}

export type SidebarItem = SidebarLink | SidebarFolder;

export interface SidebarFontConfig {
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  fontStyle: string;
  lineHeight: number;
  letterSpacing: number;
}

export interface SidebarConfig {
  items: SidebarItem[];
  fontConfig?: SidebarFontConfig;
  updatedAt: Date | FieldValue; // ✅ Allow both
}

/** Always-open section label in the Store View menu (Customers, Internal, …). */
export interface StoreViewHeader {
  type: 'header';
  id: string;
  name: string;
}

/** Pointer to a sidebar link id, used in the Store View sequence. */
export interface StoreViewLinkRef {
  type: 'link';
  id: string;
}

export type StoreViewEntry = StoreViewHeader | StoreViewLinkRef;

export function isStoreViewHeader(entry: StoreViewEntry): entry is StoreViewHeader {
  return entry?.type === 'header';
}

export function parseStoreViewEntries(data: unknown): StoreViewEntry[] {
  const raw = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  if (raw && Array.isArray(raw.entries)) {
    const out: StoreViewEntry[] = [];
    for (const item of raw.entries) {
      if (!item || typeof item !== 'object') continue;
      const e = item as Record<string, unknown>;
      if (e.type === 'header' && typeof e.id === 'string' && typeof e.name === 'string') {
        out.push({ type: 'header', id: e.id, name: e.name });
      } else if (e.type === 'link' && typeof e.id === 'string') {
        out.push({ type: 'link', id: e.id });
      }
    }
    return out;
  }
  if (raw && Array.isArray(raw.order)) {
    return raw.order
      .filter((id): id is string => typeof id === 'string')
      .map((id) => ({ type: 'link' as const, id }));
  }
  return [];
}

/** Keep headers; drop missing links; append newly granted pages at the end. */
export function reconcileStoreViewEntries(
  entries: StoreViewEntry[],
  availableLinkIds: string[]
): StoreViewEntry[] {
  const available = new Set(availableLinkIds);
  const used = new Set<string>();
  const out: StoreViewEntry[] = [];
  for (const entry of entries) {
    if (entry.type === 'header') {
      out.push(entry);
      continue;
    }
    if (available.has(entry.id) && !used.has(entry.id)) {
      out.push(entry);
      used.add(entry.id);
    }
  }
  for (const id of availableLinkIds) {
    if (!used.has(id)) out.push({ type: 'link', id });
  }
  return out;
}

// Icon mapping for dynamic icon rendering
export const ICON_MAP: Record<string, string> = {
  'dashboard': 'LayoutDashboard',
  'settings': 'Settings',
  'folder': 'Folder',
  'calendar': 'Calendar',
  'calendar-days': 'CalendarDays',
  'tasks': 'ClipboardList',
  'users': 'Users',
  'pos': 'ShoppingBag',
  'commissions': 'Receipt',
  'reports': 'FileText',
  'tickets': 'Ticket',
  'interactions': 'MessageSquare',
  'jewelry': 'Gem',
  'marketing': 'TrendingUp',
  'profile': 'User',
  'chats': 'MessageCircle',
  'camera': 'Camera',
  'gem': 'Gem',
  'package': 'Package',
  'watch': 'Watch',
  'list-checks': 'ListChecks',
  'then-now-next': 'TrendingUp',
  'trendingup': 'TrendingUp',
  'trending-up': 'TrendingUp',
  'arrow-right': 'ArrowRight',
  'clock': 'Clock',
  'timeline': 'TrendingUp',
  'inventory': 'Package',
  'mail': 'Mail',
  'email': 'Mail',
  'file-text': 'FileText',
  'file': 'File',
  'shield': 'Shield',
  'shopping-cart': 'ShoppingCart',
  'briefcase': 'Briefcase',
  'chorost': 'Briefcase',
  'database': 'Database',
  'Database': 'Database',
  'bar-chart': 'BarChart3',
  'star': 'Star',
  'banknote': 'Banknote',
  'default': 'Circle'
}; 